import React from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { logger } from "@platform/browser-logger";
import { Typography } from "@platform/design-system";

import { getCellValueInCurrentColumn } from "../../utilities/agGridUtils";

import type { PlanEditorRowData } from "../../types/values.types";

// Field configuration supplied through the cell editor params
interface PopoverListField {
  key: string;
}

interface PopoverListEditorParams {
  fields?: PopoverListField[];
  renderFormat?: string;
}

type PopoverListRendererProps = CustomCellRendererProps<
  PlanEditorRowData,
  unknown
> & {
  cellEditorParams?: PopoverListEditorParams;
};

// A single stored entry, keyed by field with primitive display values
type PopoverListEntry = Record<string, string | number>;

/**
 * Format a value based on its token type
 * Supported formats:
 * - XX: plain number
 * - $XX,XXX: currency formatting
 * - XX%: percentage
 */
const formatToken = (token: string, value: number | string): string => {
  if (!value && value !== 0) return token;

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  // Currency format: $XX,XXX
  if (token.includes("$")) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  }

  // Percentage format: XX%
  if (token.includes("%")) {
    return `${num}%`;
  }

  // Plain number: XX
  return String(num);
};

/**
 * Parse and apply renderFormat DSL
 * Examples:
 * - "XX - XX $XX,XXX" with values [35, 45, 50000] -> "35 - 45 $50,000"
 * - "XX% at XX" with values [40, 55] -> "40% at 55"
 */
const applyRenderFormat = (
  format: string,
  entry: PopoverListEntry,
  fields: PopoverListField[],
): string => {
  let result = format;

  // Extract tokens from format in the order they appear
  const tokenPattern = /(\$XX,XXX|XX%|XX)/g;
  const tokens: string[] = [];
  let match;
  while ((match = tokenPattern.exec(format)) !== null) {
    tokens.push(match[0]);
  }

  // Replace each token with its corresponding field value in order
  fields.forEach((field, index) => {
    if (index >= tokens.length) return;

    const value = entry[field.key];
    if (value === undefined || value === null || value === "") return;

    const token = tokens[index];
    const formatted = formatToken(token, value);

    // Replace the first occurrence of this specific token
    result = result.replace(token, formatted);
  });

  return result;
};

/**
 * Cell renderer for popover list fields
 * Displays a formatted list of entries
 */
export const PopoverListRenderer: React.FC<PopoverListRendererProps> = (
  props,
) => {
  const { value: rawValue, colDef, data } = props;
  logger.info("PopoverListRenderer: ", { rawValue, colDef });

  // Parse value if it's a stringified JSON array
  let value: unknown = rawValue;
  if (typeof rawValue === "string" && rawValue.trim().startsWith("[")) {
    try {
      value = JSON.parse(rawValue);
    } catch {
      value = [];
    }
  }

  // Get field configuration from cellEditorParams - passed via buildColumns
  const editorParams: PopoverListEditorParams | undefined =
    props.cellEditorParams ??
    (colDef?.cellEditorParams as PopoverListEditorParams | undefined);
  const fields = editorParams?.fields ?? [];
  const renderFormat = editorParams?.renderFormat;

  // Check if field is disabled based on editableWhen conditions
  const editableWhen = data?.benefit?.editableWhen;
  let isDisabled = false;
  if (editableWhen) {
    // Get the value from the dependent cell in the same column
    const dependentValue = getCellValueInCurrentColumn(
      props,
      editableWhen.dependsOn,
    );

    // Check if the dependent value disables this cell
    isDisabled =
      dependentValue === undefined ||
      dependentValue === null ||
      dependentValue === "" ||
      editableWhen.disabledValues?.includes(dependentValue as string);
  }

  // Show dash when disabled
  if (isDisabled) {
    return (
      <Box
        sx={{
          color: "text.disabled",
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Typography variant="body-medium">-</Typography>
      </Box>
    );
  }

  logger.info(
    `PopoverListRenderer: fields=${JSON.stringify(fields)}, renderFormat=${renderFormat}`,
  );

  if (!value || !Array.isArray(value) || value.length === 0) {
    return (
      <Box
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <EditIcon fontSize="small" sx={{ opacity: 0.5 }} />
        <Typography variant="body-medium">Click to configure</Typography>
      </Box>
    );
  }

  // Format each entry for display
  const formatEntry = (entry: PopoverListEntry): string => {
    logger.info(
      `formatEntry: keys=${Object.keys(entry)}, renderFormat=${renderFormat}`,
    );
    if (renderFormat) {
      return applyRenderFormat(renderFormat, entry, fields);
    }

    // Fallback: just join field values with " - "
    const parts: string[] = [];
    fields.forEach((field) => {
      const fieldValue = entry[field.key];
      if (
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== ""
      ) {
        parts.push(String(fieldValue));
      }
    });
    return parts.join(" - ");
  };

  // Show comma-separated values on a single line
  const formattedEntries = value.map((entry: PopoverListEntry) =>
    formatEntry(entry),
  );
  const displayText = formattedEntries.join(", ");

  return (
    <Box sx={{ cursor: "pointer", py: 0.5, width: "100%" }}>
      <Typography variant="body-medium">{displayText}</Typography>
    </Box>
  );
};
